import { RootStore } from './root.store';
import { makeAutoObservable } from 'mobx';
import { FIRST_STEP, Step } from '../domains/wizard';
import { ErrorCode } from '../domains/errorCode';
import { IErrorResponse, IOnboarding, IQuoteShortInfo } from '../domains/signup.domain';
import { IAcceptQuoteRequest, ICompanyAddress, ICustomerInformation } from '../domains/quote.domain';
import UrlParser from '../utils/url-parser';
import { SHIPPING_OPTION_DEFAULT, SHIPPING_OPTION_UNAVAILABLE } from '../domains/shipping-information.domain';
import { acceptQuote } from '../api/quote';
import axios from 'axios';
import i18n from 'i18next';
import { registerLocale } from 'react-datepicker';
import { enUS, es, fr, pt } from 'date-fns/locale';
import { DefaultLanguage, setLanguage } from '../translations/i18n';

export class SignUpStore {
  root: RootStore;

  private _activeStepIndex: number = FIRST_STEP;
  private _errorCode: ErrorCode | null = null;
  private _quoteShortInfo: IQuoteShortInfo | undefined;
  private _onboarding: IOnboarding | undefined;
  private _isLoading: boolean = false;
  showErrors: boolean = false;

  constructor(root: RootStore) {
    makeAutoObservable(this);
    this.root = root;
  }

  get activeStepIndex(): number {
    return this._activeStepIndex;
  }

  set activeStepIndex(index: number) {
    this._activeStepIndex = index;
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  async isStepValid(step: Step): Promise<boolean> {
    switch (step) {
      case Step.Onboarding:
        this._isLoading = true;
        return await this.root.onboardingInformationStore.isFormValid().finally(() => (this._isLoading = false));
      case Step.Delegate:
        return this.root.delegationInformationStore.isFormValid;
      case Step.Service:
        return this.root.serviceAddressStore.isAddressValid;
      case Step.Shipping:
        return this.root.shippingInformationStore.isFormValid;
      case Step.Billing:
        return this.root.billingInformationStore.isFormValid;
      case Step.Payment:
        return this.root.paymentInformationStore.isFormValid;
      case Step.Review:
        return this.root.reviewStore.isFormValid;
      default:
        return true;
    }
  }

  get errorCode(): ErrorCode | null {
    return this._errorCode;
  }

  setErrorCode(value: ErrorCode | null) {
    this._errorCode = value;
  }

  get quoteShortInfo(): IQuoteShortInfo | undefined {
    return this._quoteShortInfo;
  }

  setQuoteShortInfo(quoteShortInfo: IQuoteShortInfo | undefined) {
    this._quoteShortInfo = quoteShortInfo;
  }

  get onboarding(): IOnboarding | undefined {
    return this._onboarding;
  }

  setOnboarding(onboarding: IOnboarding | undefined) {
    this._onboarding = onboarding;
  }

  async signUp(): Promise<void> {
    this._isLoading = true;
    const tenantId = UrlParser.getTenantId();
    const quoteShortCode = UrlParser.getQuoteShortCode();
    const serviceAddress = this.root.serviceAddressStore.address;
    const onboardingInformation = this.root.onboardingInformationStore.information;
    const onboardingContacts = this.root.onboardingContactsStore.contacts;
    const adminContact = this.root.adminContactStore.contacts[0];
    const customerContact = this.root.billingContactsStore.contacts[0];
    const billingInformation = this.root.billingInformationStore.information;
    const paymentInformation = this.root.paymentInformationStore.information;
    const shippingContact = this.root.shippingContactStore.contacts[0];
    const request: IAcceptQuoteRequest = {
      quoteShortCode,
      customerContactFirstName: customerContact.firstName.value,
      customerContactLastName: customerContact.lastName.value,
      customerContactEmail: customerContact.email.value,
      customerStateTaxId: billingInformation.stateTaxId.value,
      invoiceAnalysisContactEmail: billingInformation.invoiceAnalysisContactEmail.value,
      invoiceAnalysisContactPhone: billingInformation.invoiceAnalysisContactPhone.value,
      invoiceDueDay: billingInformation.invoiceDueDay.value,

      signerFirstName: billingInformation.signerFirstName.value,
      signerLastName: billingInformation.signerLastName.value,
      signerNationalId: billingInformation.nationalId.value,
      signerSignatoryPosition: billingInformation.signatoryPosition.value,

      paymentMethod: paymentInformation.paymentOption.value,
      ccToken: paymentInformation.creditCardToken.value,
      ccCardHolder: paymentInformation.creditCardHolderName.value,
      payCardType: paymentInformation.creditCardType.value,
      payShipFirstName: shippingContact.firstName.value,
      payShipLastName: shippingContact.lastName.value,
      debitBankName: paymentInformation.debitCardBankName.value,
      debitAccountCode: paymentInformation.creditCardToken.value,

      customerInformation: await this.getCustomerInfo(),
      shipmentOption: this.getShipmentOption(),
      shippingContact: this.root.quoteStore.isShippingStepAvailable
        ? {
            email: shippingContact.email.value,
            firstName: shippingContact.firstName.value,
            lastName: shippingContact.lastName.value,
            phone: shippingContact.phoneNumber.value,
          }
        : undefined,
      shippingTimeRestriction: this.root.shippingInformationStore.information.timeRestriction.value,
      technicalContacts: onboardingContacts.map(x => ({
        email: x.email.value,
        firstName: x.firstName.value,
        lastName: x.lastName.value,
        phone: x.phoneNumber.value,
      })),
      estimatedShippingCharges: this.getEstimatedShippingCharges(),
      targetInstallDate: this.getUTCDate(onboardingInformation.targetInstallDate.value),
      targetPortDate: this.getUTCDate(onboardingInformation.targetPortDate.value),
      onboardingType: this.onboarding?.type,
      adminFirstName: adminContact.firstName.value,
      adminLastName: adminContact.lastName.value,
      adminEmail: adminContact.email.value,
      aiTermsOfServiceAcceptanceDate: this.root.reviewStore.information.isAiTermAccepted.value ? new Date() : undefined,
      isEquipmentOrder: this.root.quoteStore.isEquipmentOrder,
      serviceShortAddress1: serviceAddress.autocompleteShortAddress.value ?? '',
      serviceInputAddress1: serviceAddress.autocompleteInputAddress.value ?? '',
      serviceSelectedAddress1: serviceAddress.autocompleteSelectedAddress.value ?? '',
      tenantLanguage: this.root.tenantStore.config.language,
      language: i18n.language?.toLowerCase() ?? this.root.tenantStore.config.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      hasNoAdditions: this.root.quoteStore.hasNoAdditions ?? false,
      coterminousTermsOfServiceAcceptanceDate: this.root.reviewStore.information.isCoterminousTermAccepted.value ? new Date() : undefined,
    };
    try {
      const response = await acceptQuote(tenantId, quoteShortCode, request);
      this.setOnboarding({ ...this.onboarding, type: response.onboardingType });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorResponse = error.response?.data as IErrorResponse;
        this.setErrorCode(errorResponse?.code ?? ErrorCode.Undefined);
      } else {
        this.setErrorCode(ErrorCode.Undefined);
      }
    }

    this._isLoading = false;
  }

  async changeLanguage(language: string): Promise<void> {
    const languageCode = language.substring(0, 2);
    switch (languageCode) {
      case 'es':
        registerLocale('es', es);
        break;
      case 'pt':
        registerLocale('pt', pt);
        break;
      case 'fr':
        registerLocale('fr', fr);
        break;
      default:
        registerLocale(DefaultLanguage, enUS);
    }
    await this.root.tenantStore.setCountries(languageCode);
    await setLanguage(language);
  }

  private async getCustomerInfo(): Promise<ICustomerInformation> {
    const billingContacts = this.root.billingContactsStore.contacts;
    const { information } = this.root.billingInformationStore;
    const customer = this.root.quoteStore.quoteInfo?.customer;
    const { isBillingStepAvailable, isServiceStepAvailable } = this.root.quoteStore;
    const { displaySignerInformation, displayServiceInfo } = this.root.tenantStore.config;
    const customerInformation: ICustomerInformation = {
      companyName: customer?.name ?? '',
      contact1: {
        email: billingContacts[0].email.value,
        firstName: billingContacts[0].firstName.value,
        lastName: billingContacts[0].lastName.value,
        phoneNumber: billingContacts[0].phoneNumber.value,
        companyName: customer?.name ?? '',
      },
      contact2:
        billingContacts.length > 1
          ? {
              email: billingContacts[1].email.value,
              firstName: billingContacts[1].firstName.value,
              lastName: billingContacts[1].lastName.value,
              phoneNumber: billingContacts[1].phoneNumber.value,
            }
          : undefined,
      contact3:
        billingContacts.length > 2
          ? {
              email: billingContacts[2].email.value,
              firstName: billingContacts[2].firstName.value,
              lastName: billingContacts[2].lastName.value,
              phoneNumber: billingContacts[2].phoneNumber.value,
            }
          : undefined,
      customerAddress: await this.getAddress('billing'),
      serviceAddress: null,
      shippingAddress: await this.getAddress('shipping'),
      delegates: this.root.delegationInformationStore.delegates.map(m => ({
        id: m.id,
        status: m.status,
        email: m.email.value,
        name: m.fullName.value,
      })),
      email: customer?.contacts[0].email ?? '',
      firstName: customer?.contacts[0].fname ?? '',
      lastName: customer?.contacts[0].lname ?? '',
      phoneNumber: customer?.contacts[0].phone ?? '',
      taxId: customer?.customerTaxId ?? '',
    };
    if (isBillingStepAvailable) {
      const billingContact = billingContacts[0];
      customerInformation.email = billingContact.email.value;
      customerInformation.firstName = displaySignerInformation ? information.signerFirstName.value : billingContact.firstName.value;
      customerInformation.lastName = displaySignerInformation ? information.signerLastName.value : billingContact.lastName.value;
      customerInformation.phoneNumber = billingContact.phoneNumber.value;
      customerInformation.taxId = information.nationalTaxId.value;
    } else {
      const customerAddress = this.root.quoteStore.quoteInfo?.customer.address;
      if (customerAddress) {
        customerInformation.customerAddress = {
          address1: customerAddress.line1,
          address2: customerAddress.line2,
          address3: customerAddress.line3,
          neighborhood: customerAddress.neighborhood,
          streetNumber: customerAddress.streetNumber,
          zipCode: customerAddress.zip,
          city: customerAddress.city,
          state: customerAddress.state,
          stateAbbreviation: '',
          country: customerInformation.customerAddress.country,
          countryAbbreviation: customerInformation.customerAddress.countryAbbreviation,
          recipient: customerAddress.recipient ?? '',
        };
      }
    }
    if (isServiceStepAvailable) {
      customerInformation.serviceAddress = await this.getAddress('service');
    } else if (!displayServiceInfo) {
      customerInformation.serviceAddress = customerInformation.customerAddress;
    }
    return customerInformation;
  }

  private async getAddress(addressType: 'billing' | 'shipping' | 'service'): Promise<ICompanyAddress> {
    const address = this.root[`${addressType}AddressStore`].address;
    const { countries } = this.root.tenantStore;
    const country = countries.find(x => x.code === address.country.value);
    const state = await this.root.tenantStore
      .getRegions(country?.code ?? address.country.value)
      .then(states => states.find(x => x.regionCode === address.state.value));
    return {
      address1: address.address.value,
      address2: address.floor.value ? address.floor.value : address.secondaryAddress.value ?? '',
      address3: address.apartment.value ?? '',
      city: address.city.value,
      country: country?.localizedName ?? country?.name ?? address.country.value,
      state: state?.regionName ?? address.state.value,
      countryAbbreviation: country?.code ?? address.country.value,
      stateAbbreviation: state?.regionCode ?? address.state.value,
      zipCode: address.zipCode.value,
      neighborhood: address.neighborhood.value ?? '',
      streetNumber: address.streetNumber.value ?? '',
      recipient: address.recipient.value ?? '',
    };
  }

  private getShipmentOption(): string {
    if (!this.root.quoteStore.isShippingStepAvailable) return '';
    const selectedMethod = this.root.shippingInformationStore.selectedShippingMethod.method;
    let selectedShippingMethod = SHIPPING_OPTION_UNAVAILABLE;
    if (this.root.quoteStore.displayShippingOptions) {
      if (selectedMethod !== '') selectedShippingMethod = selectedMethod;
    } else {
      selectedShippingMethod =
        selectedMethod === '' || selectedMethod === SHIPPING_OPTION_UNAVAILABLE ? SHIPPING_OPTION_UNAVAILABLE : SHIPPING_OPTION_DEFAULT;
    }

    return selectedShippingMethod === SHIPPING_OPTION_UNAVAILABLE ? SHIPPING_OPTION_DEFAULT : selectedShippingMethod;
  }

  private getEstimatedShippingCharges(): number | undefined {
    const selectedShippingMethod = this.root.shippingInformationStore.selectedShippingMethod;
    if (this.root.quoteStore.displayShippingOptions) {
      if (selectedShippingMethod.method && selectedShippingMethod.method !== SHIPPING_OPTION_UNAVAILABLE) {
        return selectedShippingMethod.price;
      }
    }

    return undefined;
  }

  private getUTCDate(date: Date | null): string {
    if (!date) return '';
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const utcDate = new Date(Date.UTC(year, month, day));
    return utcDate.toISOString();
  }
}
